/**
 * « Même terme » sans alignement (chantier C23 « Confrontation », décision 3) : deux
 * concepts se font face dès que leurs clés sont égales une fois la casse, les
 * accents, les blancs de bord et les espaces multiples repliés — la règle
 * `exactKeyword` du mobile, partagée ici pour le bureau. La clé seule : ni les
 * autres formes, ni les libellés cachés (faux appariements, règle inexplicable à
 * l'écran). Plus tard, un alignement posé (SP4) vaudra « même terme » quel que soit
 * le nom.
 *
 * `marqueValideeDuConcept` lit la marque dans le JSON de la note (jamais dans
 * l'index `cles`, qui ne dit pas la validation) : c'est la condition que le serveur
 * applique aussi (`porteUneMarqueValidee`, `api/src/utils/apercuConcept.ts`).
 */
import type { Entree } from '../types/api';
import { blockTexts, marksInBlock } from '../components/mobile/notes/entreeRange';
import { estValidee } from './provenance';
import { cleRepliee } from './cleRepliee';

/**
 * Clé repliée : accents, casse, blancs de bord et espaces multiples ignorés. La règle vit
 * dans `cleRepliee.ts` (copié dans l'API pour la découverte « Face à face avec… », C23 SP6).
 */
export const foldKey = (s: string): string => cleRepliee(s);

/** Deux clés disent-elles le même terme ? Une clé vide ne fait face à rien. */
export function memeTerme(a: string, b: string): boolean {
  const ka = foldKey(a);
  return ka !== '' && ka === foldKey(b);
}

/** Mon concept dont la clé dit le même terme que `cle`, le premier dans l'ordre du store. */
export function monConceptDeMemeCle(cle: string, entrees: readonly Entree[]): Entree | undefined {
  const k = foldKey(cle);
  if (!k) return undefined;
  return entrees.find(e => foldKey(e.cle) === k);
}

/**
 * La note porte-t-elle une marque VALIDÉE de ce concept, par identifiant ?
 * JSON absent ou inexploitable → `false`. Il suffit qu'une occurrence soit validée.
 */
export function marqueValideeDuConcept(contentJSON: unknown, entreeId: string): boolean {
  if (!entreeId || contentJSON === undefined || contentJSON === null) return false;
  const nombreDeBlocs = blockTexts(contentJSON).length;
  for (let block = 0; block < nombreDeBlocs; block++) {
    for (const mark of marksInBlock(contentJSON, block)) {
      if (mark.entree.id === entreeId && estValidee(mark.entree.item ?? {})) return true;
    }
  }
  return false;
}
